import dayjs from 'dayjs'
import { useCallback } from 'react'

import { dateToMonthYear } from '../../utilities/date'
import useEventActions from '../useEventActions/useEventActions'
import useServices from '../useServices'
import useSubscriptionPlans from '../useSubscriptionPlans'
import useTherapists from '../useTherapists'
import useUser from '../useUser'
import {
  getFreeSubscriptionEventsForMonthYear,
  getNextAvailableMonth,
  getSubscriptionForMonthYear,
  getUnusedCarryOverInSubscription,
} from './useSubscription.utils'

const useSubscription = (initPrice?: number) => {
  const { isLoadingTherapists, therapists } = useTherapists('visible')
  const { services, isLoadingServices } = useServices()
  const { usedSubscriptionEvents } = useEventActions()
  const { user } = useUser()

  const { allSubscriptions, visibleSubscriptions } = useSubscriptionPlans(initPrice)

  const userSubscriptions = user.userSubscriptions

  const getFreeSubscriptionEvents = useCallback(
    (monthYear: string) => getFreeSubscriptionEventsForMonthYear(userSubscriptions, usedSubscriptionEvents, monthYear),
    [userSubscriptions, usedSubscriptionEvents],
  )
  const getUsedSubscriptionEvents = useCallback(
    (monthYear: string) => {
      const subscription = getSubscriptionForMonthYear(userSubscriptions, monthYear)
      return (usedSubscriptionEvents[subscription?.id] || {})[monthYear] || 0
    },
    [usedSubscriptionEvents, userSubscriptions],
  )
  const getNextMonthWithAvailableSession = useCallback(
    () => getNextAvailableMonth(userSubscriptions, usedSubscriptionEvents),
    [userSubscriptions, usedSubscriptionEvents],
  )
  const getSubscription = useCallback(
    (monthYear: string) => getSubscriptionForMonthYear(userSubscriptions, monthYear),
    [userSubscriptions],
  )
  const getUnusedCarryOver = useCallback(
    (monthYear: string) => {
      const subscription = getSubscriptionForMonthYear(userSubscriptions, monthYear)
      if (!subscription) return 0
      return getUnusedCarryOverInSubscription(subscription, usedSubscriptionEvents)
    },
    [userSubscriptions, usedSubscriptionEvents],
  )

  const activeSubscription = getSubscription(dateToMonthYear(dayjs().add(1, 'month')))

  const getMonthYearsOverLimit = useCallback(
    (limit: number) => {
      return Object.keys(usedSubscriptionEvents[activeSubscription.id] || {}).reduce(
        (overLimit, monthYear) => [...overLimit, ...(usedSubscriptionEvents[monthYear] > limit ? [monthYear] : [])],
        [],
      )
    },
    [usedSubscriptionEvents, activeSubscription],
  )

  const alreadyChangedForNextMonth = !!userSubscriptions?.find(
    (subscription) => subscription.validFrom === dayjs().startOf('month').add(1, 'month').toISOString(),
  )

  const therapist =
    !!activeSubscription && therapists?.find((therapist) => therapist.therapistId === activeSubscription?.therapistId)
  const service = !!activeSubscription && services?.find((service) => service.id === activeSubscription?.serviceId)

  const lastTransaction = activeSubscription?.transactions?.reduce((last, curr) =>
    dayjs(curr.subscriptionPeriodFrom).isAfter(dayjs(last.subscriptionPeriodFrom)) ? curr : last,
  )?.subscriptionPeriodFrom

  const isLoadingTherapist = isLoadingTherapists
  const isLoadingSubscription = isLoadingTherapist || isLoadingServices || !allSubscriptions

  return {
    allSubscriptions,
    alreadyChangedForNextMonth,
    isLoadingSubscription,
    isLoadingTherapist,
    therapist,
    service,
    getSubscription,
    getFreeSubscriptionEvents,
    getUsedSubscriptionEvents,
    activeSubscription,
    getMonthYearsOverLimit,
    getUnusedCarryOver,
    getNextMonthWithAvailableSession,
    lastTransaction,
    visibleSubscriptions,
  }
}

export default useSubscription
